import { useQuery } from '@tanstack/react-query';
import { Dialog } from 'sharedUi/Dialog';
import type { EntityDependencies } from '../../types/rbac';

type ChipTone = 'slate' | 'emerald' | 'indigo' | 'rose';

const TONE: Record<ChipTone, string> = {
  slate: 'bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-gray-200',
  emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
  indigo: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300',
  rose: 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300',
};

/** A titled card block used for each relationship group. */
export const LinkCard = ({
  icon,
  title,
  count,
  children,
}: {
  icon: string;
  title: string;
  count?: number;
  children: React.ReactNode;
}) => (
  <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
    <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5 dark:border-gray-700">
      <div className="flex items-center gap-2">
        <i className={`${icon} text-indigo-600`} />
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
      </div>
      {count !== undefined && (
        <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300">
          {count}
        </span>
      )}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

/** Wrapped chip list; renders an em dash when empty. */
export const LinkChips = ({ items, tone = 'slate' }: { items: string[]; tone?: ChipTone }) => {
  if (!items.length) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item, i) => (
        <span
          key={`${item}-${i}`}
          className={`inline-flex items-center rounded-md px-2 py-0.5 font-mono text-[11px] ${TONE[tone]}`}
        >
          {item}
        </span>
      ))}
    </div>
  );
};

// The "used by" (upstream) half, fetched from the dependencies endpoint.
const UsedBySection = ({
  entityId,
  resourceKey,
  getDependencies,
}: {
  entityId: string;
  resourceKey: string;
  getDependencies: (id: string) => Promise<EntityDependencies>;
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['rbac', 'dependencies', resourceKey, entityId],
    queryFn: () => getDependencies(entityId),
    enabled: !!entityId,
    staleTime: 0,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-ink-muted">
        <i className="pi pi-spin pi-spinner" /> Loading references...
      </div>
    );
  }
  if (isError || !data) {
    return <p className="text-xs text-gray-400">Could not load references.</p>;
  }

  const d = data.dependents;
  if (data.total === 0) {
    return (
      <LinkCard icon="pi pi-arrow-up-right" title="Used by">
        <p className="text-xs text-gray-400">Not referenced by any other RBAC entity.</p>
      </LinkCard>
    );
  }

  return (
    <>
      {d.permissionSets.length > 0 && (
        <LinkCard icon="pi pi-clone" title="Used by Permission Sets" count={d.permissionSets.length}>
          <LinkChips items={d.permissionSets.map((x) => x.key || x.displayName || x._id)} tone="indigo" />
        </LinkCard>
      )}
      {d.roles.length > 0 && (
        <LinkCard icon="pi pi-users" title="Used by Roles" count={d.roles.length}>
          <LinkChips items={d.roles.map((x) => x.key || x.displayName || x._id)} tone="indigo" />
        </LinkCard>
      )}
      {d.roleBindings.length > 0 && (
        <LinkCard icon="pi pi-link" title="Used by Role Bindings" count={d.roleBindings.length}>
          <LinkChips items={d.roleBindings.map((x) => `${x.role ?? '?'} @ ${x.unit ?? 'Global'}`)} tone="indigo" />
        </LinkCard>
      )}
      {d.assignments.length > 0 && (
        <LinkCard icon="pi pi-id-card" title="Used by User Assignments" count={d.assignments.length}>
          <LinkChips items={d.assignments.map((x) => x.user || x._id)} tone="indigo" />
        </LinkCard>
      )}
    </>
  );
};

interface EntityLinksDialogProps {
  /** Record whose links to show; null closes the dialog. */
  entityId: string | null;
  title: string;
  /** Cache discriminator for the dependencies lookup. */
  resourceKey: string;
  /** Optional upstream lookup. Omit for leaf resources (e.g. assignments). */
  getDependencies?: (id: string) => Promise<EntityDependencies>;
  onHide: () => void;
  /** Downstream section ("what this contains"), composed by the caller with LinkCard/LinkChips. */
  children?: React.ReactNode;
}

/**
 * Read-only dialog that shows a record's related RBAC entities:
 *  - `children` renders the downstream side ("what this contains / grants").
 *  - When `getDependencies` is provided, the upstream side ("used by …") is
 *    fetched and rendered below it.
 */
const EntityLinksDialog = ({
  entityId,
  title,
  resourceKey,
  getDependencies,
  onHide,
  children,
}: EntityLinksDialogProps) => (
  <Dialog
    visible={!!entityId}
    onHide={onHide}
    header={title}
    style={{ width: '640px', maxWidth: '95vw' }}
    modal
  >
    <div className="flex flex-col gap-3 pt-1">
      {children}
      {getDependencies && entityId && (
        <UsedBySection entityId={entityId} resourceKey={resourceKey} getDependencies={getDependencies} />
      )}
    </div>
  </Dialog>
);

export default EntityLinksDialog;
